import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import { Panel, PanelBody } from "../../components/panel/panel";
import {
  getThemeGradientButtonStyle,
  onThemeGradientButtonEnter,
  onThemeGradientButtonLeave,
} from "../../utils/themeGradientButtonStyle";

const emptyForm = {
  useCustomSender: false,
  senderEmail: "",
  senderDisplayName: "",
  replyToEmail: "",
  sesVerificationStatus: "Pending",
};

const normalizeSettings = (data) => ({
  useCustomSender: data?.useCustomSender ?? data?.UseCustomSender ?? false,
  senderEmail: data?.senderEmail ?? data?.SenderEmail ?? "",
  senderDisplayName: data?.senderDisplayName ?? data?.SenderDisplayName ?? "",
  replyToEmail: data?.replyToEmail ?? data?.ReplyToEmail ?? "",
  sesVerificationStatus: data?.sesVerificationStatus ?? data?.SesVerificationStatus ?? "Pending",
});

const EmailSenderSettingsPage = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [form, setForm] = useState(emptyForm);
  const [testEmail, setTestEmail] = useState("");
  const [compName, setCompName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const loadSettings = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const [settingsRes, profileRes] = await Promise.all([
        axiosInstance.get(APIs.GET_COMPANY_EMAIL_SETTINGS, { params: { CompID } }),
        axiosInstance.get(APIs.GET_COMPANY_BOOTSTRAP_PROFILE),
      ]);

      const data = settingsRes.data?.data ?? settingsRes.data?.Data ?? {};
      const profile = profileRes.data?.data ?? profileRes.data?.Data ?? {};
      const name = profile.compName ?? profile.CompName ?? "";
      setCompName(name);

      const normalized = normalizeSettings(data);
      setForm({
        ...normalized,
        senderDisplayName: normalized.senderDisplayName || name,
      });
      setTestEmail(profile.compEmail ?? profile.CompEmail ?? "");
    } catch {
      showErrorToast("Failed to load email sender settings.");
      setForm(emptyForm);
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const statusLabel = () => {
    if (!form.useCustomSender || !form.senderEmail.trim()) {
      return { text: "Using ServeIQ default sender", className: "bg-secondary" };
    }
    if (form.sesVerificationStatus === "Verified") {
      return { text: "Verified in AWS SES", className: "bg-success" };
    }
    return { text: "Pending AWS verification", className: "bg-warning text-dark" };
  };

  const handleSave = async () => {
    if (!CompID) {
      showErrorToast("Company context is missing. Please sign in again.");
      return;
    }

    if (form.useCustomSender && !form.senderEmail.trim()) {
      showErrorToast("Sender email is required when custom sender is enabled.");
      return;
    }

    try {
      setSaving(true);
      const response = await axiosInstance.post(APIs.POST_ADD_UPDATE_COMPANY_EMAIL_SETTINGS, {
        compID: Number(CompID),
        useCustomSender: form.useCustomSender,
        senderEmail: form.senderEmail.trim() || null,
        senderDisplayName: form.senderDisplayName.trim() || null,
        replyToEmail: form.replyToEmail.trim() || null,
      });

      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Could not save email sender settings.");
        return;
      }

      showSuccessToast(response.data?.message || "Email sender settings saved successfully.");
      await loadSettings();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Something went wrong while saving email sender settings."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      showErrorToast("Please enter a recipient email for the test.");
      return;
    }

    try {
      setSendingTest(true);
      const response = await axiosInstance.post(APIs.POST_COMPANY_EMAIL_SEND_TEST, {
        toEmail: testEmail.trim(),
      });

      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Failed to send test email.");
        return;
      }

      showSuccessToast(response.data?.message || "Test email sent successfully.");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to send test email.");
    } finally {
      setSendingTest(false);
    }
  };

  const panelInnerStyle = {
    backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
    border: isDarkMode ? "1px solid #404040" : undefined,
    color: isDarkMode ? "#ffffff" : undefined,
  };

  const gradientBtnStyle = getThemeGradientButtonStyle(themeColor);
  const badge = statusLabel();

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Email Sender Settings</li>
          </ol>
          <h1 className="page-header mb-0">Email Sender Settings</h1>
          <p
            className="mb-0 mt-1 small"
            style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}
          >
            From address and Reply-To used for customer emails.
          </p>
        </div>
      </div>

      <GridSectionLoader loading={loading || saving} minHeight="320px">
        <div className="card border-0">
          <Panel className="m-3">
            <PanelBody>
              <div className="p-4 rounded-3 shadow-sm" style={panelInnerStyle}>
                <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
                  Configure the From address, display name, and Reply-To used when sending order,
                  reservation, and marketing emails to your customers. Until your sender is verified
                  in AWS SES, ServeIQ&apos;s default email address is used automatically.
                </p>

                <div className="mb-3">
                  <span className={`badge ${badge.className}`}>{badge.text}</span>
                </div>

                <div className="mb-4">
                  <ToggleSwitch
                    isChecked={form.useCustomSender}
                    onToggle={() =>
                      setForm((prev) => ({ ...prev, useCustomSender: !prev.useCustomSender }))
                    }
                    label={form.useCustomSender ? "Use custom sender email" : "Use ServeIQ default sender"}
                  />
                </div>

                {form.useCustomSender && (
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <FormInput
                        label="Sender email (From)"
                        name="senderEmail"
                        type="email"
                        value={form.senderEmail}
                        onchange={(e) =>
                          setForm((prev) => ({ ...prev, senderEmail: e.target.value }))
                        }
                        placeHolder="orders@yourrestaurant.com"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <FormInput
                        label="Display name"
                        name="senderDisplayName"
                        type="text"
                        value={form.senderDisplayName}
                        onchange={(e) =>
                          setForm((prev) => ({ ...prev, senderDisplayName: e.target.value }))
                        }
                        placeHolder={compName || "Your restaurant name"}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <FormInput
                        label="Reply-To email"
                        name="replyToEmail"
                        type="email"
                        value={form.replyToEmail}
                        onchange={(e) =>
                          setForm((prev) => ({ ...prev, replyToEmail: e.target.value }))
                        }
                        placeHolder={form.senderEmail || "Same as sender email"}
                      />
                    </div>
                  </div>
                )}

                <div className="d-flex flex-wrap gap-2 mt-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                    style={gradientBtnStyle}
                    onMouseEnter={onThemeGradientButtonEnter}
                    onMouseLeave={onThemeGradientButtonLeave}
                  >
                    {saving ? "Saving…" : "Save settings"}
                  </button>
                </div>

                <hr className="my-4" />

                <h6 className="mb-3">Send test email</h6>
                <div className="row align-items-end">
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Recipient"
                      name="testEmail"
                      type="email"
                      value={testEmail}
                      onchange={(e) => setTestEmail(e.target.value)}
                      placeHolder="you@example.com"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleSendTest}
                      disabled={sendingTest}
                    >
                      {sendingTest ? "Sending…" : "Send test email"}
                    </button>
                  </div>
                </div>
              </div>
            </PanelBody>
          </Panel>
        </div>
      </GridSectionLoader>
    </div>
  );
};

export default EmailSenderSettingsPage;
